import { css } from '@emotion/react';
import React, { CSSProperties } from 'react';

import { cn } from '@/common/helpers';
import { useTheme } from '@/ui/theme';

import { FieldErrorMessage, Label } from '.';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  description?: string;
  style?: CSSProperties;
  withBottomMargin?: boolean;
  isRequired?: boolean;
  hideError?: boolean;
  error?: string;
  value?: string;
  children?: React.ReactNode;
  className?: string;
  rightIcon?: React.ReactNode;
}

export const Input = React.forwardRef(
  (
    {
      label,
      description,
      name,
      style,
      withBottomMargin,
      isRequired,
      hideError,
      error,
      value,
      children,
      className,
      rightIcon,
      ...rest
    }: InputProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const fieldId = `field-${name}`;
    const {
      colors,
      fonts,
      keylines,
      text,
      forms: { input },
    } = useTheme();

    const containerStyles = css`
      display: flex;
      flex-direction: column;
      flex: 1;
      ${input?.position && `position: ${input.position};`}

      margin-bottom: ${withBottomMargin ? '30px' : '0'};

      &.error {
        label {
          color: ${colors.error};
        }
        input {
          border-color: ${colors.error};
        }
        .error-message {
          visibility: visible;
        }

        &.empty {
          ::placeholder {
            color: ${colors.error};
          }
        }
      }

      .input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
      }

      input {
        padding: ${input?.padding ?? '16px'};
        outline: none;
        display: block;
        background: ${input?.backgroundColor ?? 'transparent'};
        color: ${input?.color ?? colors.textPrimary};

        font-family: ${input?.fontFamily ?? fonts.body};
        font-size: ${input?.fontSize ?? text.bodyCopy.fontSize};
        font-weight: ${input?.fontWeight ?? text.bodyCopy.fontWeight ?? 400};
        border: ${input?.border ?? keylines?.border};
        border-radius: ${input?.borderRadius ?? '4px'};
        line-height: 24px;

        /* Ensure inputs fill the width of their container as a general rule: */
        width: 100%;

        &:focus {
          outline: 1px solid ${colors.textSecondary};
          outline-offset: 2px;
        }

        &:read-only {
          user-select: none;
        }

        ::placeholder {
          color: ${input?.placeholder?.color ?? '#718096'};
        }

        &:disabled {
          background: ${input?.disabled?.background ?? '#e6e6e6'};
        }
      }

      .right-icon {
        position: absolute;
        right: 16px;
        display: flex;
        align-items: center;
        pointer-events: none;
      }
    `;

    const showError = !!error;
    const isEmpty = value && value.length <= 0;

    return (
      <div
        css={containerStyles}
        style={style}
        className={cn(showError && 'error', isEmpty && 'empty', className)}
      >
        {label && (
          <Label
            htmlFor={fieldId}
            isRequired={isRequired}
            description={description}
          >
            {label}
          </Label>
        )}
        <div className="input-wrapper">
          <input
            ref={ref}
            name={name}
            id={fieldId}
            // TODO: check if removing `data-test` doesn't break anything
            data-test={fieldId}
            data-testid={fieldId}
            aria-describedby={hideError ? undefined : `${fieldId}-error`}
            aria-required={isRequired}
            value={value}
            {...rest}
          />
          {rightIcon && <div className="right-icon">{rightIcon}</div>}
        </div>
        {children ? children : null}
        {hideError ? null : (
          <FieldErrorMessage
            fieldName={name}
            message={error}
            isVisible={showError}
          />
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
